import type React from 'react';

import type { Body, Meta, Uppy } from '@uppy/core';

import { Button, Text } from '@change/ds-core-ui';
import { UploadIcon } from '@change/ds-icons';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';
import { VisuallyHidden } from '@change-corgi/design-system-legacy/a11y';
import { Box } from '@change-corgi/design-system-legacy/layout';

import { AccessibleFileInputWrapperSunriseV2 as AccessibleFileInputWrapper } from './AccessibleFileInputWrapperSunriseV2';

type Props = {
	disabled?: boolean;
	onButtonClick?: () => void;
	uppy: Uppy<Meta, Body>;
	fileInputId?: string;
};

export const ImageUploader: React.FC<Props> = ({
	disabled = false,
	onButtonClick,
	uppy,
	fileInputId = 'file-input-id',
}) => {
	const { translate } = useI18n();
	const uploadAPhotoText = { strings: { chooseFiles: translate('corgi.components.image_upload.button') } };

	return (
		<div className="image-uploader h-[272px]">
			<Box
				variant="bordered"
				sx={{
					borderStyle: 'dashed',
					height: '100%',
					borderColor: 'sunrise.semantic.stroke.strong',
				}}
			>
				<div className="media-upload-content-wrapper flex h-full flex-col items-center justify-center gap-4">
					<Box sx={{ lineHeight: 0, display: ['none', 'block'] }}>
						<UploadIcon data-testid="drop-target" size={48} className="text-icon-weak h-12 w-12" />
					</Box>
					<Box id="sap-media-view">
						<VisuallyHidden as="span">
							<Translate value="corgi.sap.media.a11y.uploader.accessible_hidden_div" />
						</VisuallyHidden>
						{!disabled ? (
							<AccessibleFileInputWrapper
								uploadButtonText={uploadAPhotoText.strings.chooseFiles}
								uppy={uppy}
								fileInputId={fileInputId}
								onButtonClick={onButtonClick}
							/>
						) : (
							<Button disabled={disabled} data-qa="media-images-upload" variant="secondary" size="small">
								<Text size="small">{uploadAPhotoText.strings.chooseFiles}</Text>
							</Button>
						)}
					</Box>
				</div>
			</Box>
		</div>
	);
};

ImageUploader.displayName = 'ImageUploader';
